import { createSignal, type Accessor } from "solid-js";
import type { AdminApi } from "../api/client";
import type { QuotaView, Relay } from "../model";

export type QuotaLoadStatus = "idle" | "loading" | "ready" | "refreshing" | "error";

export interface QuotaEntry {
  status: QuotaLoadStatus;
  data: QuotaView | null;
  error: unknown;
  updatedAt: number | null;
}

export interface QuotaBoard {
  entries: Accessor<Record<string, QuotaEntry>>;
  entry: (relayID: string) => QuotaEntry;
  load: (relayID: string) => Promise<QuotaView | null>;
  loadActive: (relays: Relay[], concurrency?: number) => Promise<void>;
}

const idleEntry: QuotaEntry = { status: "idle", data: null, error: null, updatedAt: null };

export function createQuotaBoard(api: AdminApi): QuotaBoard {
  const [entries, setEntries] = createSignal<Record<string, QuotaEntry>>({});
  const generations = new Map<string, number>();

  function entry(relayID: string): QuotaEntry {
    return entries()[relayID] ?? idleEntry;
  }

  function update(relayID: string, next: QuotaEntry): void {
    setEntries((current) => ({ ...current, [relayID]: next }));
  }

  async function load(relayID: string): Promise<QuotaView | null> {
    const current = entry(relayID);
    const generation = (generations.get(relayID) ?? 0) + 1;
    generations.set(relayID, generation);
    update(relayID, {
      ...current,
      status: current.data === null ? "loading" : "refreshing",
      error: null,
    });
    try {
      const data = await api.relays.quota(relayID);
      if (generations.get(relayID) !== generation) return null;
      update(relayID, { status: "ready", data, error: null, updatedAt: Date.now() });
      return data;
    } catch (error) {
      if (generations.get(relayID) !== generation) return null;
      update(relayID, {
        status: "error",
        data: current.data,
        error,
        updatedAt: current.updatedAt,
      });
      return null;
    }
  }

  async function loadActive(relays: Relay[], concurrency = 4): Promise<void> {
    const queue = relays.filter((relay) => relay.state === "active");
    let next = 0;
    async function worker(): Promise<void> {
      while (next < queue.length) {
        const relay = queue[next];
        next += 1;
        if (relay) await load(relay.id);
      }
    }
    const count = Math.min(Math.max(1, concurrency), queue.length);
    await Promise.all(Array.from({ length: count }, () => worker()));
  }

  return { entries, entry, load, loadActive };
}
